import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { CoreStore } from '@core/store/core.store';
import { authPageEvents } from '@core/store/core.events';
import { injectDispatch } from '@ngrx/signals/events';
import { StorageService } from '@core/services/storage.service';
import { ConfigService } from '@core/services/config.service';
import { AlertComponent } from '@nesea/ngx-ui-kit/alert';
import { ButtonComponent } from '@nesea/ngx-ui-kit/button';
import { InputGroupComponent } from '@nesea/ngx-ui-kit/form/shared';
import { AuthAsideComponent } from '@shared/components/auth-aside/auth-aside.component';
import { SESSION_EXPIRED_ERROR } from '@core/store/core.models';

@Component({
  selector: 'nsf-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  imports: [
    TranslocoModule,
    ReactiveFormsModule,
    AlertComponent,
    ButtonComponent,
    InputGroupComponent,
    AuthAsideComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginComponent {

  private _coreStore = inject(CoreStore);
  private _dispatch = injectDispatch(authPageEvents);
  private _storageService: StorageService = inject(StorageService);
  private _configService: ConfigService = inject(ConfigService);

  protected readonly loginError = this._coreStore.loginError;
  protected readonly supportEmail = computed(() => this._configService.config.contact?.technicalSupportEmail ?? '');
  protected readonly isSessionExpired = computed(() => ['invalid_grant', SESSION_EXPIRED_ERROR].includes(this.loginError()?.error ?? ''));
  protected readonly showCorrelation = computed(() => this.loginError()?.error !== SESSION_EXPIRED_ERROR);

  /**
   * Copy keys for the three page states: clean login, expired session, enrolling failure.
   * One single source so title/subtitle/aside/alert/Microsoft info stay consistent.
   */
  protected readonly loginCopy = computed(() => {
    if(!this.loginError()) {
      return {
        title: 'LOGIN.TITLE',
        subtitle: 'LOGIN.SUBTITLE',
        asideTitle: 'AUTH.BRAND.TITLE',
        asideSubtitle: 'AUTH.BRAND.SUBTITLE',
        alertTitle: '',
        alertDescription: '',
        microsoftInfo: 'LOGIN.MICROSOFT_INFO'
      };
    }

    return this.isSessionExpired()
      ? {
        title: 'LOGIN.SESSION_ERROR.TITLE',
        subtitle: 'LOGIN.SESSION_ERROR.SUBTITLE',
        asideTitle: 'AUTH.BRAND.SESSION_ERROR_TITLE',
        asideSubtitle: 'AUTH.BRAND.SESSION_ERROR_SUBTITLE',
        alertTitle: 'LOGIN.SESSION_ERROR.ALERT_TITLE',
        alertDescription: 'LOGIN.SESSION_ERROR.ALERT_DESCRIPTION',
        microsoftInfo: 'LOGIN.SESSION_ERROR.MICROSOFT_INFO'
      }
      : {
        title: 'LOGIN.ERROR.TITLE',
        subtitle: 'LOGIN.ERROR.SUBTITLE',
        asideTitle: 'AUTH.BRAND.ERROR_TITLE',
        asideSubtitle: 'AUTH.BRAND.ERROR_SUBTITLE',
        alertTitle: 'LOGIN.ERROR.ALERT_TITLE',
        alertDescription: 'LOGIN.ERROR.ALERT_DESCRIPTION',
        microsoftInfo: 'LOGIN.ERROR.MICROSOFT_INFO'
      };
  });

  protected readonly emailControl = new FormControl(this._storageService.loginHintEmail, {
    nonNullable: true,
    validators: [Validators.email]
  });

  protected readonly isEmailValid = signal(this.emailControl.valid);
  protected readonly isEmailTouched = signal(false);
  protected readonly showEmailError = computed(() => this.isEmailTouched() && this.emailControl.invalid);

  protected readonly canContinue = computed(() => this.isEmailValid());

  onEmailInput(): void {
    this.isEmailValid.set(this.emailControl.valid);
  }

  onEmailBlur(): void {
    this.isEmailTouched.set(true);
  }

  onContinue(): void {
    if(this.emailControl.invalid) {
      this.isEmailTouched.set(true);
      return;
    }

    const email: string = this.emailControl.value.trim();
    this._dispatch.authorizeRequested({ loginHint: email || undefined });
  }

  onMicrosoft(): void {
    // "Switch account" only for profile/enrollment errors: an expired session must
    // re-enter with the user's own account.
    const useSelectAccount: boolean = !!this.loginError() && !this.isSessionExpired();
    this._dispatch.authorizeRequested(useSelectAccount ? { prompt: 'select_account' } : {});
  }

}